import { computed, shallowRef, type Ref } from "vue";
import { assertNever } from "../lib/assertNever";
import { isJust } from "../lib/maybe";
import { readStringPref, writeStringPref } from "../lib/localPrefs";
import { dispatchInto } from "../lib/tea";
import {
  initialThemeModel,
  updateTheme,
  type Theme,
  type ThemeCmd,
  type ThemeModel,
} from "./useTheme.update";

export type { Theme };

function readTheme(): Theme {
  if (typeof document === "undefined") return "light";
  if (document.documentElement.classList.contains("dark")) return "dark";

  const stored = readStringPref("theme");
  if (isJust(stored) && (stored.value === "light" || stored.value === "dark")) {
    return stored.value;
  }

  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

const holder = shallowRef<ThemeModel>(initialThemeModel(readTheme()));

function runThemeCmd(cmd: ThemeCmd): void {
  switch (cmd.kind) {
    case "applyTheme":
      document.documentElement.classList.toggle("dark", cmd.theme === "dark");
      writeStringPref("theme", cmd.theme);
      return;
    default:
      assertNever(cmd as never);
  }
}

function dispatchTheme(msg: Parameters<typeof updateTheme>[1]): void {
  void dispatchInto(holder, msg, updateTheme, runThemeCmd);
}

export function useTheme(): {
  theme: Ref<Theme>;
  toggleTheme: () => void;
} {
  // Stay in sync if the early HTML script already set the class.
  const current = readTheme();
  if (current !== holder.value.theme) {
    dispatchTheme({ type: "Set", theme: current });
  }

  const theme = computed({
    get: (): Theme => holder.value.theme,
    set: (next: Theme): void => {
      dispatchTheme({ type: "Set", theme: next });
    },
  });

  function toggleTheme(): void {
    dispatchTheme({ type: "Toggle" });
  }

  return { theme, toggleTheme };
}
